import { useState, useRef } from 'react';
import { 
  Paperclip, 
  FilePdf, 
  Image as ImageIcon, 
  X as XClose, 
  CloudArrowUp, 
  Trash, 
  Warning 
} from '@phosphor-icons/react';
import {
  addPendingAttachment,
  removePendingAttachment,
  hasPendingDuplicate,
} from '../../services/attachmentCache';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_ATTACHMENTS = 3;
const ALLOWED_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
];

/**
 * PendingAttachmentUploader - Handles attachments for new events
 * Files are stored locally in IndexedDB until the event is saved
 */
export default function PendingAttachmentUploader({ 
  pendingAttachments = [], 
  onPendingChange,
  tempId,
  disabled = false,
}) {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  
  const canUpload = pendingAttachments.length < MAX_ATTACHMENTS && !disabled;
  
  function formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  
  async function validateFile(file) {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'File type not allowed. Use PDF, PNG, JPEG, or WebP';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File too large. Maximum size is 5MB';
    }
    if (await hasPendingDuplicate(tempId, file.name)) {
      return `A file named "${file.name}" is already attached`;
    }
    if (pendingAttachments.some(p => p.filename === file.name)) {
      return `A file named "${file.name}" is already attached`;
    }
    return null;
  }
  
  async function handleFileSelect(file) {
    const validationError = await validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setError(null);
    
    try {
      const pending = await addPendingAttachment(file, tempId, 'event');
      onPendingChange([...pendingAttachments, pending]);
    } catch (err) {
      console.error('Failed to add pending attachment:', err);
      setError('Failed to add file');
    }
  }
  
  function handleInputChange(e) {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    e.target.value = '';
  }
  
  async function handleRemove(pendingTempId) {
    try {
      await removePendingAttachment(pendingTempId);
      onPendingChange(pendingAttachments.filter(p => p.tempId !== pendingTempId));
    } catch (err) {
      console.error('Failed to remove pending attachment:', err);
      setError('Failed to remove file');
    }
  }
  
  function handleDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    if (canUpload) setIsDragging(true);
  }
  
  function handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }
  
  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
  }
  
  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (!canUpload) return;
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }
  
  function getFileIcon(fileType) {
    if (fileType === 'application/pdf') {
      return <FilePdf size={24} weight="fill" className="text-red-500" />;
    }
    if (fileType?.startsWith('image/')) {
      return <ImageIcon size={24} weight="fill" className="text-blue-500" />;
    }
    return <Paperclip size={24} />;
  }
  
  return (
    <div className="space-y-3">
      {/* Existing pending attachments */}
      {pendingAttachments.length > 0 && (
        <div className="space-y-2">
          {pendingAttachments.map((item) => (
            <div 
              key={item.tempId}
              className="flex items-center gap-3 p-3 rounded-sm"
              style={{ backgroundColor: 'var(--color-bg-surface)' }}
            >
              {getFileIcon(item.fileType)}
              
              <div className="flex-1 min-w-0">
                <p 
                  className="text-sm font-medium truncate"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {item.filename}
                </p>
                <p 
                  className="text-xs"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  {formatFileSize(item.fileSize)} • Will upload on save
                </p>
              </div>
              
              <button
                type="button"
                onClick={() => handleRemove(item.tempId)}
                disabled={disabled}
                className="p-2 rounded-sm transition-colors disabled:opacity-50 hover:bg-black/5"
                style={{ color: 'var(--color-text-muted)' }}
                title="Remove"
              >
                <Trash size={18} />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Upload area */}
      {canUpload && (
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => !disabled && fileInputRef.current?.click()}
          className={`
            relative border-2 border-dashed rounded-sm p-4 text-center cursor-pointer transition-colors
            ${isDragging ? 'border-solid' : ''}
          `}
          style={{
            borderColor: isDragging ? 'var(--color-brand-primary)' : 'var(--color-border-subtle)',
            backgroundColor: isDragging ? 'rgba(93, 144, 133, 0.1)' : 'transparent',
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp"
            onChange={handleInputChange}
            className="hidden"
            disabled={disabled}
          />
          
          <CloudArrowUp 
            size={32} 
            weight={isDragging ? 'fill' : 'regular'}
            style={{ color: isDragging ? 'var(--color-brand-primary)' : 'var(--color-text-muted)' }}
            className="mx-auto mb-2"
          />
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            {isDragging ? (
              'Drop file here'
            ) : (
              <>
                <span style={{ color: 'var(--color-brand-primary)' }}>Click to upload</span>
                {' or drag and drop'}
              </>
            )}
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
            PDF, PNG, JPEG, WebP (max 5MB)
          </p>
        </div>
      )}
      
      {/* Limit message */}
      {pendingAttachments.length >= MAX_ATTACHMENTS && (
        <p className="text-xs text-center" style={{ color: 'var(--color-text-muted)' }}>
          Maximum {MAX_ATTACHMENTS} attachments reached
        </p>
      )}
      
      {/* Error message */}
      {error && (
        <div 
          className="flex items-center gap-2 p-3 rounded-sm text-sm"
          style={{ 
            backgroundColor: 'rgba(193, 96, 92, 0.1)',
            color: 'var(--color-status-error)',
          }}
        >
          <Warning size={18} weight="fill" />
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-auto p-1 hover:opacity-70"
          >
            <XClose size={16} />
          </button>
        </div>
      )}
      
      {/* Info message */}
      <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
        💡 Files are stored locally and will be uploaded when you save the event.
      </p>
    </div>
  );
}
